import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Star, Sun, Moon, Sunrise, Sunset, IndianRupee, TrendingUp, Calendar, Upload, Image, Trash2, X, Loader2, Camera, Plus, CalendarDays } from 'lucide-react';
import api from '../../../../lib/api';
import { toast } from 'sonner';
import { useConfirmDialog } from '../../../../components/ui/ConfirmDialog';

function ReviewsTab({ reviews }) {
  const avgRating = reviews.length ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : '0.0';
  return (
    <div className="space-y-4">
      <h2 className="font-cinzel text-lg font-semibold text-maroon">Reviews</h2>
      <div className="bg-white rounded-xl p-6 text-center">
        <p className="text-4xl font-bold text-maroon">{avgRating}</p>
        <div className="flex justify-center gap-1 my-2">{[1,2,3,4,5].map(s => <Star key={s} className={`w-5 h-5 ${s <= Math.round(parseFloat(avgRating)) ? 'text-gold fill-gold' : 'text-gray-300'}`} />)}</div>
        <p className="text-gray-500">{reviews.length} reviews</p>
      </div>
      {reviews.length === 0 ? <p className="text-center text-gray-400 py-8">No reviews yet</p> : (
        <div className="space-y-3">{reviews.map(r => (
          <div key={r.id} className="bg-white rounded-xl p-4">
            <div className="flex justify-between"><span className="font-medium">{r.user_name}</span><div className="flex gap-0.5">{[1,2,3,4,5].map(s => <Star key={s} className={`w-4 h-4 ${s <= r.rating ? 'text-gold fill-gold' : 'text-gray-300'}`} />)}</div></div>
            <p className="text-sm text-gray-600 mt-2">{r.comment}</p>
          </div>
        ))}</div>
      )}
    </div>
  );
}


function PortfolioTab({ portfolio, onRefresh }) {
  const [showModal, setShowModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newItem, setNewItem] = useState({ title: '', description: '', type: 'image', url: '' });
  const fileInputRef = useRef(null);
  const { confirm, ConfirmDialog } = useConfirmDialog();

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'video/mp4', 'video/webm'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image or video file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB.');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/api/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setNewItem({ 
        ...newItem, 
        url: response.data.url,
        type: file.type.startsWith('video') ? 'video' : 'image'
      });
      toast.success('File uploaded!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleAddItem = async () => {
    if (!newItem.url) {
      toast.error('Please upload an image or video');
      return;
    }

    try {
      await api.post('/api/priest/portfolio', {
        title: newItem.title || 'Untitled',
        description: newItem.description,
        type: newItem.type,
        url: newItem.url
      });
      toast.success('Portfolio item added!');
      setNewItem({ title: '', description: '', type: 'image', url: '' });
      setShowModal(false);
      onRefresh();
    } catch (error) {
      toast.error('Failed to add item');
    }
  };

  const handleDelete = async (itemId) => {
    const ok = await confirm({
      title: 'Delete Portfolio Item?',
      message: 'This item will be permanently removed from your portfolio.',
      confirmLabel: 'Delete',
      variant: 'danger'
    });
    if (!ok) return;
    try {
      await api.delete(`/api/priest/portfolio/${itemId}`);
      toast.success('Item deleted');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleAddClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowModal(true);
  };

  return (
    <div className="space-y-4">
      <ConfirmDialog />
      <div className="flex items-center justify-between">
        <h2 className="font-cinzel text-lg font-semibold text-maroon">Portfolio</h2>
        <button 
          type="button"
          onClick={handleAddClick}
          className="flex items-center gap-2 bg-saffron text-white px-4 py-2 rounded-lg text-sm hover:bg-saffron/90 active:scale-95 transition-all cursor-pointer"
        >
          <Upload className="w-4 h-4" /> Add
        </button>
      </div>
      {portfolio.length === 0 ? (
        <div className="bg-white rounded-xl p-8 text-center">
          <Image className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-400">No portfolio items</p>
          <p className="text-sm text-gray-300 mt-1">Add photos and videos of your work</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {portfolio.map(p => (
            <div key={p.id} className="aspect-square rounded-xl bg-gray-100 overflow-hidden relative group">
              {p.type === 'video' ? (
                <video src={p.url} className="w-full h-full object-cover" />
              ) : (
                <img src={p.url} alt={p.title} className="w-full h-full object-cover" />
              )}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <button
                  onClick={() => handleDelete(p.id)}
                  className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              {p.title && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                  <p className="text-white text-xs truncate">{p.title}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add Portfolio Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl max-w-lg w-full"
          >
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="font-cinzel text-lg font-semibold text-maroon">Add Portfolio Item</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Upload Image/Video*</label>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm"
                  className="hidden"
                />
                
                {newItem.url ? (
                  <div className="relative">
                    {newItem.type === 'video' ? (
                      <video src={newItem.url} className="w-full h-48 object-cover rounded-xl" controls />
                    ) : (
                      <img src={newItem.url} alt="Preview" className="w-full h-48 object-cover rounded-xl" />
                    )}
                    <button
                      onClick={() => setNewItem({ ...newItem, url: '' })}
                      className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="w-full p-8 border-2 border-dashed border-gray-300 rounded-xl hover:border-saffron hover:bg-orange-50 transition-colors flex flex-col items-center gap-2"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-8 h-8 animate-spin text-saffron" />
                        <span className="text-sm text-gray-500">Uploading...</span>
                      </>
                    ) : (
                      <>
                        <Camera className="w-8 h-8 text-gray-400" />
                        <span className="text-sm text-gray-500">Click to upload</span>
                        <span className="text-xs text-gray-400">Images or Videos (max 10MB)</span>
                      </>
                    )}
                  </button>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title (optional)</label>
                <input
                  type="text"
                  value={newItem.title}
                  onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-saffron/50"
                  placeholder="e.g., Satyanarayan Pooja at Mumbai"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                <textarea
                  value={newItem.description}
                  onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-saffron/50"
                  placeholder="Add details about this work..."
                />
              </div>
            </div>
            
            <div className="p-4 border-t flex justify-end gap-3">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-600">Cancel</button>
              <button
                onClick={handleAddItem}
                disabled={!newItem.url || uploading}
                className="px-6 py-2 bg-saffron text-white rounded-xl disabled:opacity-50 flex items-center gap-2"
              >
                <Plus className="w-4 h-4" /> Add to Portfolio
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}


function PanchangTab({ panchang, festivals }) {
  const getPanchangValue = (v, f = '') => { if (!v) return f; if (typeof v === 'string') return v; if (typeof v === 'object') return v.name || v.value || f; return String(v); };
  return (
    <div className="space-y-4">
      <h2 className="font-cinzel text-lg font-semibold text-maroon">Panchang & Calendar</h2>
      <div className="bg-white rounded-xl p-4 md:p-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2"><Sun className="w-5 h-5 text-saffron" /> Today's Panchang</h3>
        {panchang ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[{l:'Tithi',v:panchang.tithi},{l:'Nakshatra',v:panchang.nakshatra},{l:'Yoga',v:panchang.yoga},{l:'Karana',v:panchang.karana},{l:'Sunrise',v:panchang.sunrise},{l:'Sunset',v:panchang.sunset}].map((i,idx) => (
              <div key={idx} className="bg-gray-50 rounded-xl p-3"><p className="text-xs text-gray-500">{i.l}</p><p className="font-medium">{getPanchangValue(i.v, '--')}</p></div>
            ))}
          </div>
        ) : <p className="text-center text-gray-400 py-4">Loading...</p>}
      </div>
      <div className="bg-white rounded-xl p-4 md:p-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2"><CalendarDays className="w-5 h-5 text-saffron" /> Upcoming Festivals</h3>
        <div className="space-y-2">{festivals.slice(0,10).map((f,i) => (
          <div key={i} className="flex justify-between items-center p-3 bg-orange-50 rounded-xl">
            <div><p className="font-medium">{f.name}</p><p className="text-sm text-gray-500">{f.date}</p></div>
            {f.days_until !== undefined && <span className={`px-3 py-1 rounded-full text-sm ${f.days_until === 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{f.days_until === 0 ? 'Today!' : `${f.days_until} days`}</span>}
          </div>
        ))}</div>
      </div>
    </div>
  );
}

function EarningsTab({ earnings, analytics }) {
  const totalEarnings = earnings?.total_earnings || analytics?.stats?.total_earnings || 0;
  const monthlyEarnings = earnings?.monthly_earnings || analytics?.stats?.monthly_earnings || 0;
  const classEarnings = earnings?.class_earnings || 0;
  const pendingPayouts = earnings?.pending_payouts || 0;
  const bookingEarnings = totalEarnings - classEarnings;

  return (
    <div className="space-y-6" data-testid="priest-earnings-tab">
      <div>
        <h2 className="font-cinzel text-2xl font-bold text-maroon">Earnings & Payouts</h2>
        <p className="text-gray-500 text-sm">Track your bookings, class income, and payouts</p>
      </div>

      {/* Summary Cards */}
      <div className="grid md:grid-cols-4 grid-cols-2 gap-4">
        <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-4 text-white">
          <p className="text-sm opacity-80">Net Earnings</p>
          <p className="text-2xl font-bold">₹{totalEarnings.toLocaleString()}</p>
        </div>
        <div className="bg-gradient-to-br from-saffron to-orange-500 rounded-2xl p-4 text-white">
          <p className="text-sm opacity-80">This Month</p>
          <p className="text-2xl font-bold">₹{monthlyEarnings.toLocaleString()}</p>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-4 text-white">
          <p className="text-sm opacity-80">Pending Payout</p>
          <p className="text-2xl font-bold">₹{pendingPayouts.toLocaleString()}</p>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-violet-600 rounded-2xl p-4 text-white">
          <p className="text-sm opacity-80">Class Income</p>
          <p className="text-2xl font-bold">₹{classEarnings.toLocaleString()}</p>
        </div>
      </div>

      {/* Income Sources */}
      <div className="bg-white rounded-2xl p-6 border border-orange-100">
        <h3 className="font-cinzel text-lg font-semibold text-maroon mb-4">Income Sources</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between py-3 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-saffron/10 rounded-xl flex items-center justify-center text-saffron text-lg">🙏</div>
              <div>
                <p className="font-medium text-gray-800">Puja Bookings</p>
                <p className="text-xs text-gray-500">Completed ceremonies & consultations</p>
              </div>
            </div>
            <p className="font-bold text-green-600 text-lg">₹{bookingEarnings.toLocaleString()}</p>
          </div>
          <div className="flex items-center justify-between py-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center text-purple-600 text-lg">📚</div>
              <div>
                <p className="font-medium text-gray-800">Vedic Classes</p>
                <p className="text-xs text-gray-500">Student enrollment fees</p>
              </div>
            </div>
            <p className="font-bold text-purple-600 text-lg">₹{classEarnings.toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Transactions */}
      <div className="bg-white rounded-2xl p-6 border border-orange-100">
        <h3 className="font-cinzel text-lg font-semibold text-maroon mb-4">Recent Transactions</h3>
        {(!earnings?.transactions || earnings.transactions.length === 0) ? (
          <p className="text-center text-gray-400 py-8">No transactions yet</p>
        ) : (
          <div className="space-y-3">
            {earnings.transactions.slice(0, 15).map((t, i) => (
              <div key={i} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    t.type === 'class' ? 'bg-purple-50 text-purple-600' : 'bg-green-50 text-green-600'
                  }`}>
                    {t.type === 'class' ? '📚' : '🙏'}
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">{t.service_name || t.description || 'Service'}</p>
                    <p className="text-xs text-gray-500">
                      {t.date ? new Date(t.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                    </p>
                  </div>
                </div>
                <p className="font-bold text-green-600">+₹{t.amount}</p>
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-gray-400 mt-4">
          Payouts are processed after service completion. Contact support for payout queries.
        </p>
      </div>
    </div>
  );
}


export { ReviewsTab, PortfolioTab, PanchangTab, EarningsTab };
